import { useCallback, useRef, useState } from 'react'

import { Flex } from '../../../../Components'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import {
  AddServiceButton,
  ServiceEmptyState,
  SingleServiceContainer,
  TransferField
} from '../Components'
import {
  addService,
  getElementsByService,
  getServiceCount,
  readKey,
  removeService
} from '../Functions'
import { SectionShell } from '../SectionShell'
import { useGigiFormValidation } from '../ValidationContext'
import { getDiffStatus } from './Functions'
import { useMultiServiceZodValidation } from './multiServiceValidation'
import { buildPlaneServiceSchema } from './Plane.schema'
const language = cap.language.state || 'IT'
const t = translations[language]

const {
  plane: {
    typeTrip,
    companyName,
    classType,
    seatPreference,
    departureDate,
    airportDeparture,
    fromHourDeparture,
    toHourDeparture,
    luggageType,
    luggageWeight,
    luggageNumber,
    arrivalDate,
    airportArrival,
    fromHourReturn,
    toHourReturn,
    typeBoarding,
    companyCode,
    noteAggiuntive = ''
  }
} = getElementsByService()

const SECTION_ID = 'plane'

export const PlaneSection = ({
  state: [state, setState],
  readOnly,
  diff,
  enableArrivoEntro = true
}) => {
  const [valTick, setValTick] = useState(0)
  const trackingSetState = (data, forceRender) => {
    setState(data, forceRender)
    setValTick((v) => v + 1)
  }
  const stateProp = [state, trackingSetState]
  const statePropRender = [state, (data) => trackingSetState(data, true)]
  const containerRef = useRef(null)
  const { sectionHasErrors } = useGigiFormValidation()

  const planeCount = getServiceCount(state, 'plane')

  const buildSchema = useCallback(
    () => buildPlaneServiceSchema({ enableArrivoEntro }),
    [enableArrivoEntro]
  )
  const buildServiceValues = useCallback(
    (formState, i) => ({
      typeTrip: readKey(formState, typeTrip.location, i) ?? '',
      departureDate: readKey(formState, departureDate.location, i) ?? '',
      airportDeparture: readKey(formState, airportDeparture.location.value, i) ?? '',
      airportArrival: readKey(formState, airportArrival.location.value, i) ?? '',
      arrivalDate: readKey(formState, arrivalDate.location, i) ?? '',
      fromHourDeparture: readKey(formState, fromHourDeparture.location, i) ?? '',
      toHourDeparture: readKey(formState, toHourDeparture.location, i) ?? '',
      fromHourReturn: readKey(formState, fromHourReturn.location, i) ?? '',
      toHourReturn: readKey(formState, toHourReturn.location, i) ?? '',
      companyName: readKey(formState, companyName.location, i) ?? '',
      classType: readKey(formState, classType.location, i) ?? '',
      seatPreference: readKey(formState, seatPreference.location, i) ?? '',
      luggageType: readKey(formState, luggageType.location, i) ?? '',
      luggageNumber: readKey(formState, luggageNumber.location, i) ?? '',
      luggageWeight: readKey(formState, luggageWeight.location, i) ?? '',
      typeBoarding: readKey(formState, typeBoarding.location, i) ?? '',
      companyCode: readKey(formState, companyCode.location, i) ?? '',
      noteAggiuntive: readKey(formState, noteAggiuntive.location, i) ?? ''
    }),
    []
  )

  useMultiServiceZodValidation({
    sectionId: SECTION_ID,
    containerRef,
    state,
    serviceCount: planeCount,
    buildSchema,
    buildServiceValues,
    trigger: valTick
  })

  const getProps = (stateRender, item, key, count, readOnly, diff) => ({
    state: stateRender ? statePropRender : stateProp,
    item,
    key,
    count,
    readOnly,
    diffStatus: getDiffStatus(item, diff, count)
  })

  return (
    <SectionShell
      id="plane"
      icon="plane"
      title={t.serviziAereo?.toUpperCase() || 'AEREO'}
      storageKey="gigiForm.section.plane"
      hasErrors={sectionHasErrors('plane')}
      count={planeCount}
      contentId="plane-content"
      headerExtra={
        !readOnly && (
          <AddServiceButton
            label={t.aggiungiServizio}
            onClick={async () => setState(await addService(state, 'plane'))}
          />
        )
      }
    >
      <div ref={containerRef}>
        <Flex fw>
          {!planeCount && (
            <ServiceEmptyState
              icon="plane"
              title={t.nessunServizioAereoInserito}
              hint={t.servizioEmptyHint}
            />
          )}
          {Array(getServiceCount(state, 'plane'))
            .fill()
            .map((_, i) => {
              const departureDateValue = readKey(state, departureDate?.location, i)
              const minDateArrival = departureDateValue
                ? new Date(
                    new Date(
                      departureDateValue.split('/')[2],
                      departureDateValue.split('/')[1] - 1,
                      departureDateValue.split('/')[0]
                    )
                  )
                : new Date()
              const isRoundTrip = readKey(state, typeTrip?.location, i) === 'RT'
              return (
                <SingleServiceContainer
                  key={i}
                  index={i}
                  serviceType="plane"
                  state={state}
                  onRemove={() => setState(removeService(state, 'plane', i))}
                  readOnly={readOnly}
                >
                  <TransferField {...getProps(true, typeTrip, 'typeTrip', i, readOnly, diff)} />
                  <TransferField
                    {...getProps(false, companyName, 'companyName', i, readOnly, diff)}
                  />
                  <TransferField {...getProps(false, classType, 'classType', i, readOnly, diff)} />
                  <TransferField
                    {...getProps(false, seatPreference, 'seatPreference', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(true, departureDate, 'departureDate', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(false, airportDeparture, 'airportDeparture', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(false, fromHourDeparture, 'fromHourDeparture', i, readOnly, diff)}
                  />
                  {!!enableArrivoEntro && (
                    <TransferField
                      {...getProps(false, toHourDeparture, 'toHourDeparture', i, readOnly, diff)}
                    />
                  )}

                  {/*
                  Interruzione di layout: senza break, in flex-wrap la 4ª colonna (data ritorno)
                  finiva sulla stessa riga dei tre campi bagaglio.
                */}
                  <div aria-hidden className="ff-service-grid--break" />
                  {isRoundTrip && (
                    <div
                      className="ff-service-grid--full"
                      style={{
                        marginTop: 'var(--space-2)',
                        marginBottom: 'var(--space-1)',
                        fontFamily: 'var(--font-family)',
                        fontWeight: 'var(--font-weight-bold)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-muted)'
                      }}
                    >
                      {t.gfReturnLeg}
                    </div>
                  )}
                  <TransferField
                    {...getProps(
                      false,
                      { ...arrivalDate, notRequired: !isRoundTrip, minDate: minDateArrival },
                      'arrivalDate',
                      i,
                      readOnly || !isRoundTrip,
                      diff
                    )}
                  />
                  <TransferField
                    {...getProps(false, airportArrival, 'airportArrival', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(
                      false,
                      fromHourReturn,
                      'fromHourReturn',
                      i,
                      readOnly || !isRoundTrip,
                      diff
                    )}
                  />
                  {!!enableArrivoEntro && (
                    <TransferField
                      {...getProps(false, toHourReturn, 'toHourReturn', i, readOnly, diff)}
                    />
                  )}
                  <div aria-hidden className="ff-service-grid--break" />
                  <TransferField
                    {...getProps(false, luggageType, 'luggageType', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(false, luggageNumber, 'luggageNumber', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(false, luggageWeight, 'luggageWeight', i, readOnly, diff)}
                  />
                  <div aria-hidden className="ff-service-grid--break" />
                  <TransferField
                    {...getProps(false, typeBoarding, 'typeBoarding', i, readOnly, diff)}
                  />
                  <TransferField
                    {...getProps(false, companyCode, 'companyCode', i, readOnly, diff)}
                  />
                  <div aria-hidden className="ff-service-grid--break" />
                  <div className="ff-service-grid--full">
                    <TransferField
                      {...getProps(false, noteAggiuntive, 'noteAggiuntive', i, readOnly, diff)}
                    />
                  </div>
                </SingleServiceContainer>
              )
            })}
        </Flex>
      </div>
    </SectionShell>
  )
}
